<template>
  <div class="filter-wrapper" v-if="showFilters">
    <div class="filter-container">
      <!-- Search -->
      <div
        class="search-filter filter-box"
        :aria-label="t('CASE_FILTER.aria.searchCases')"
        role="search"
      >
        <i class="fa-solid fa-magnifying-glass"></i>
        <input
          type="search"
          v-model="searchQuery"
          :placeholder="t('CASE_FILTER.searchPlaceholder')"
        />
      </div>

      <!-- Token Filter -->
      <button
        class="token-filter filter-box"
        :class="{ active: isTokenMode }"
        @click="toggleMode"
        type="button"
        :aria-label="t('CASE_FILTER.aria.toggleTokenCases')"
      >
        <i class="fa-solid fa-ticket"></i>
        {{ t('CASE_FILTER.tokenCases') }}
      </button>

      <!-- Balance Filter -->
      <button
        class="balance-filter filter-box"
        :class="{ active: balanceCapActive }"
        @click="applyBalanceCap"
        type="button"
        :aria-label="t('CASE_FILTER.aria.toggleBalance')"
      >
        <i class="fa-solid fa-wallet"></i>
        {{ t('CASE_FILTER.enoughBalance') }}
      </button>

      <!-- Discount Filter -->
      <button
        class="discount-filter filter-box"
        :class="{ active: isDiscountedMode }"
        @click="toggleDiscountMode"
        type="button"
        aria-label="Toggle Discounted Cases"
      >
        <i class="fa-solid fa-percent"></i>
        Discounted Cases
      </button>

      <!-- Price Range -->
      <div class="price-range filter-box hide-on-mobile" :aria-label="t('CASE_FILTER.aria.priceRange')">
        <div>{{ priceRange[0] }}</div>
        <div class="slider-wrap" style="z-index: 10">
          <VueSlider
            v-model="priceRange"
            :min="min"
            :max="max"
            :interval="step"
            :lazy="true"
            :enable-cross="false"
            :dot-size="12"
            :height="6"
            :process-style="{ backgroundColor: '#4caf50' }"
            :rail-style="{ backgroundColor: '#b0bec5' }"
            :dot-style="{ borderColor: '#4caf50' }"
          />
        </div>
        <div>{{ priceRange[1] }}</div>
      </div>

      <!-- Clear -->
      <div class="hide-on-mobile" v-if="hasActiveFilters" >
        <button
          class="filter-box"
          type="button"
          @click="resetFilters"
          :aria-label="t('CASE_FILTER.aria.resetFilters')"
          :title="t('CASE_FILTER.clear')"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
// Components
import VueSlider from 'vue-3-slider-component'

// Stores & composables
import { useProfileStore } from '@/store/profile'
import { useCasesStore } from '@/store/cases'
import { useAppStore } from '@/store/app'
import { useI18n } from 'vue-i18n'

const { t, n } = useI18n()

defineOptions({ name: 'CaseFilter' })

// --- constants ---
const DEFAULT_MAX = 1000

// --- stores ---
const profileStore = useProfileStore()
const casesStore = useCasesStore()
const appStore = useAppStore()

const { profile } = storeToRefs(profileStore)
const { filters } = storeToRefs(casesStore)
const { showFilters } = storeToRefs(appStore)

// --- local state ---
const min = ref(0)
const max = ref(DEFAULT_MAX)
const step = ref(5)
const balanceCapActive = ref(false)

// --- computed ---
type Range = [number, number]

const priceRange = computed<Range>({
  get: () => (filters.value.priceRange as Range) ?? [0, DEFAULT_MAX],
  set: (v) => casesStore.setFilters({ priceRange: v }),
})

const searchQuery = computed<string>({
  get: () => filters.value.searchTerm ?? '',
  set: (value: string) => casesStore.setFilters({ searchTerm: value }),
})

const isTokenMode = computed(() => filters.value.mode === 'token')
const isDiscountedMode = computed(() => filters.value.isDiscounted)
const hasActiveFilters = computed(() => {
  const isPriceRangeDefault = 
    priceRange.value[0] === 0 && priceRange.value[1] === DEFAULT_MAX
  const hasSearch = searchQuery.value.trim().length > 0
  
  return (
    isTokenMode.value ||
    balanceCapActive.value ||
    !isPriceRangeDefault ||
    isDiscountedMode.value ||
    hasSearch
  )
})

// --- actions ---
function toggleMode() {
  casesStore.setFilters({ mode: isTokenMode.value ? 'all' : 'token' })

  if (isTokenMode) {
    balanceCapActive.value = false
    casesStore.setFilters({ priceRange: [0, DEFAULT_MAX] })
  }
}

function applyBalanceCap() {
  balanceCapActive.value = !balanceCapActive.value
  if (balanceCapActive.value) {
    let cap = Math.floor((profile.value.balance ?? 0) / 100)
    if (cap > DEFAULT_MAX) {
      cap = DEFAULT_MAX
    }
    casesStore.setFilters({ priceRange: [0, cap] })
  } else {
    casesStore.setFilters({ priceRange: [0, DEFAULT_MAX] })
  }
}

function resetFilters() {
  casesStore.setFilters({
    mode: 'all',
    priceRange: [0, DEFAULT_MAX],
    isDiscounted: false,
    searchTerm: '',
  })
  balanceCapActive.value = false
}

function toggleDiscountMode() {
  casesStore.setFilters({ isDiscounted: !isDiscountedMode.value })
}
</script>

<style lang="scss" scoped>
.filter-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 1em;
  margin-bottom: 2.5rem;

  @include mobile {
    display: none;
  }

  .filter-container {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin: 0 auto;
    background-color: $elevation1;
    padding: 0.5em;
    border-radius: 8px;

    .filter-box {
      display: inline-flex;
      align-items: center;
      gap: 0.5em;
      font-size: 0.9em;
      background-color: $surface;
      padding: 0.5em 1em;
      border-radius: 8px;
      cursor: pointer;
      border: 1px solid transparent;
      color: $text-light-100;
      input {
        background: transparent;
        border: none;
        outline: none;
        color: inherit;
        width: 100%;
        font-size: 1em;
      }
    }
    .filter-box.active {
      border-color: #4caf50;
    }

    .search-filter {
      flex: 0 0 240px;
      min-width: 200px;
      cursor: text;
    }

    .price-range {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1em;
      width: 400px;
    }

    .price-range .slider-wrap {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }
  }
}

@include mobile {
  .filter-wrapper {

    .filter-container {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5em;

      .price-range {
        width: 100%;
      }
      .search-filter {
        width: 100%;
      }
    }
  }
}
</style>
